import React, {useEffect, useId} from 'react';
import {DragDropProvider, useDraggable, useDroppable} from '@dnd-kit/react';
import {closestCenter, defaultCollisionDetection} from '@dnd-kit/collision';
import {CollisionPriority, Draggable} from '@dnd-kit/abstract';

import styles from './index.module.styl';
import {handleListReorder} from './utils';
import {DraggableEntryProps, DragMetadata, EntryGenerator, EntrySetter} from './types';

function handleAccepts<T extends DragMetadata>(source: Draggable<T>, accepts: DraggableEntryProps<T>[`accepts`]) {
  if (!source.type || typeof source.type !== `string` || !accepts) {
    return false;
  }

  const fn = accepts[source.type as T[`type`]];
  if (!fn) {
    return false;
  }

  return fn(source.data as T) ?? false;
}

/**
 * A drop target for sorting draggable entries. This is used to create a drop zone above and below the entry.
 */
export function DraggableEntrySortDropTarget<T extends DragMetadata>(props: DraggableEntryProps<T>) {
  const dragId = useId();

  const {ref, isDropTarget: isDropping} = useDroppable<DragMetadata>({
    id: dragId,
    collisionDetector: closestCenter,
    collisionPriority: CollisionPriority.Normal,
    type: props.data.type,
    data: props.data,
    accept: (source) => {
      // ensures entries within the same group and type can be sorted
      return (
        source.data.group === props.data.group || (props.sortAccepts ? handleAccepts(source, props.sortAccepts) : false)
      );
    },
  });

  return (
    <div className={styles.dropTarget} ref={ref} data-drop-zone>
      {isDropping && <div className={styles.indicator} />}
    </div>
  );
}

/**
 * Provides a generic interface for making components draggable.
 *
 * Passes in the following refs to the child component:
 *
 * - `ref` specifies the draggable area of the component
 * - `dropRef` specifies the droppable area of the component when receiving a drop operation
 * - `handleRef` specifies where the component can be dragged from
 */
export function DraggableEntry<T extends DragMetadata>(props: DraggableEntryProps<T>) {
  const element = React.Children.only(props.children);
  const data = props.data;

  const id = useId();
  const ref = React.useRef<HTMLDivElement>(null);
  const dragId = `${id}-${data.index}`;

  const {ref: draggableRef, handleRef} = useDraggable({
    id: `${dragId}-draggable`,
    feedback: `clone`,
    type: data.type,
    disabled: props.disabled,
    data,
  });

  /**
   * This is the droppable zone target for the entry itself
   * it allows dropping other items onto the entry with
   */
  const {ref: droppableRef, isDropTarget} = useDroppable({
    collisionDetector: defaultCollisionDetection,
    collisionPriority: CollisionPriority.Normal,
    id: `${dragId}-droppable`,
    type: data.type,
    data,

    accept(source) {
      return handleAccepts(source, props.accepts);
    },
  });

  if (!props.children || !React.isValidElement(props.children)) {
    throw new Error(`DraggableEntry must have exactly one child`);
  }

  useEffect(() => {
    draggableRef(ref.current);
    if (!props.useDropRef) {
      droppableRef(ref.current);
    }
  }, [ref.current]);

  useEffect(() => {
    ref.current?.toggleAttribute(`data-dnd-is-drop-target`, isDropTarget);
  }, [isDropTarget]);

  return (
    <div className={styles.draggableEntry} data-draggable-entry='true'>
      {!props.disableSortZones && <DraggableEntrySortDropTarget {...props} />}

      {React.cloneElement(element, {
        ref,
        handleRef: !props.disabled ? handleRef : undefined,
        dropRef: props.useDropRef ? droppableRef : undefined,
      })}

      {/* droppable target for bottom of the list  */}
      {props.isLast && !props.disableSortZones && (
        <DraggableEntrySortDropTarget {...props} data={{...data, index: data.index + 1}} />
      )}
    </div>
  );
}

/**
 * Provides a basic implementation of a draggable list with arbitrary content.
 *
 * Each item in the list will receive a `handleRef` prop that can be used to attach a drag handle and
 * must export a `ref` prop to be used as the draggable element.
 */
export function DraggableEntriesList(props: {
  entries: Obj[];
  children: Array<React.ReactElement<typeof DraggableEntry>>;
  setEntries: EntrySetter;
  entryGenerator?: EntryGenerator;
}) {
  return (
    <DragDropProvider
      onDragEnd={(event) => {
        handleListReorder({
          event,
          entries: props.entries,
          entryGenerator: props.entryGenerator,
          setEntries: props.setEntries,
        });
      }}
    >
      {/* renders all sortable items */}
      {props.children}
    </DragDropProvider>
  );
}
